import { useState, useEffect, useRef } from "react";
import {
  useContentStore,
  type ProductDetail,
  type ProductSpec,
  type ProductCategory,
} from "@/hooks/useContentStore";

const ACCENT_OPTIONS = [
  { value: "cyan", label: "青色", cls: "bg-cyan-400" },
  { value: "orange", label: "橙色", cls: "bg-orange-400" },
  { value: "emerald", label: "绿色", cls: "bg-emerald-400" },
  { value: "violet", label: "紫色", cls: "bg-violet-400" },
];

function FieldInput({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[#8aaec8] text-xs">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
        />
      )}
    </div>
  );
}

function ImageField({
  label,
  value,
  onChange,
  aspectRatio = "4/3",
  storageKey,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  aspectRatio?: "16/9" | "4/3" | "1/1" | "21/9";
  storageKey: string;
}) {
  const [inputVal, setInputVal] = useState(value);
  const [preview, setPreview] = useState(value);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgSize, setImgSize] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFileSize, setUploadedFileSize] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputVal(value);
    setPreview(value);
    if (!value) {
      setUploadedFileName(null);
      setUploadedFileSize(0);
    } else if (value.startsWith("data:")) {
      setUploadedFileName("已内嵌图片");
    }
  }, [storageKey, value]);

  const handleApply = () => {
    setErrored(false);
    setLoading(true);
    setPreview(inputVal);
    onChange(inputVal);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoading(false);
    const img = e.currentTarget;
    setImgSize(`${img.naturalWidth} × ${img.naturalHeight}`);
  };

  const handleImageError = () => {
    setLoading(false);
    setErrored(true);
    setImgSize("");
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const url = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("文件读取失败"));
        reader.readAsDataURL(file);
      });
      setUploadedFileName(file.name);
      setUploadedFileSize(file.size);
      setInputVal(url);
      setPreview(url);
      setErrored(false);
      onChange(url);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLocal = async () => {
    setUploadedFileName(null);
    setUploadedFileSize(0);
    setInputVal("");
    setPreview("");
    setErrored(false);
    onChange("");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const ratioClass = {
    "21/9": "aspect-[21/9]",
    "16/9": "aspect-video",
    "4/3": "aspect-[4/3]",
    "1/1": "aspect-square",
  }[aspectRatio];

  const isEmpty = !preview || errored;

  return (
    <div className="bg-[#0a1628] border border-[#1a2840] rounded-xl overflow-hidden">
      {/* Preview area */}
      <div
        className={`relative w-full ${ratioClass} bg-[#060a12] overflow-hidden group ${
          dragOver ? "ring-2 ring-[#4fc3f7]/50" : ""
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {!isEmpty ? (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#060a12]">
                <i className="ri-loader-4-line text-[#4fc3f7] text-2xl animate-spin" />
              </div>
            )}
            <img
              src={preview}
              alt={label}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {/* Overlay info bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 py-2.5 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-[10px] font-mono">{imgSize}</span>
                {uploadedFileName && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] border border-emerald-500/30">
                    内嵌
                  </span>
                )}
              </div>
              <a
                href={preview}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4fc3f7] text-[10px] hover:text-white transition-colors flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="ri-external-link-line" />
                查看原图
              </a>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#3a5a7a]">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d1529] border border-[#1e2d4a]">
              <i className="ri-image-line text-xl" />
            </div>
            <span className="text-xs">{errored ? "图片加载失败，请检查链接" : "暂无图片预览"}</span>
            <span className="text-[10px] text-[#2a4a6a]">拖拽图片到此处或点击下方上传</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[#8aaec8] text-xs">{label}</p>
          {uploadedFileName && (
            <div className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] border border-emerald-500/20">
                已写入内容
              </span>
              <button
                onClick={handleDeleteLocal}
                className="text-[#5a7a9a] hover:text-red-400 transition-colors cursor-pointer"
                title="移除当前图片"
              >
                <i className="ri-delete-bin-line text-xs" />
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApply()}
            placeholder="粘贴图片 URL..."
            className="flex-1 min-w-0 bg-[#070c1a] border border-[#1e2d4a] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
          />
          <button
            onClick={handleApply}
            className="px-3 py-1.5 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            应用
          </button>
          {inputVal && (
            <button
              onClick={() => { setInputVal(""); setPreview(""); setErrored(false); onChange(""); }}
              className="px-2 py-1.5 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer"
              title="清空"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
          <span className="text-[#3a5a7a] text-[10px]">或</span>
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={uploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} />
            {uploading ? "上传中..." : "上传图片文件"}
          </button>
          {uploadedFileName && (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <i className="ri-image-line text-cyan-400 text-sm flex-shrink-0" />
              <span className="text-gray-400 text-xs truncate">{uploadedFileName}</span>
              {uploadedFileSize > 0 && (
                <span className="flex-shrink-0 text-[10px] text-gray-500">
                  {(uploadedFileSize / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
            </div>
          )}
          {!uploadedFileName && <span className="text-[#3a5a7a] text-xs">支持 JPG / PNG / WebP / GIF</span>}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
              e.target.value = "";
            }}
          />
        </div>

        {errored && (
          <p className="text-red-400 text-[10px] flex items-center gap-1">
            <i className="ri-error-warning-line" />
            无法加载该图片，请检查 URL 是否可访问
          </p>
        )}
      </div>
    </div>
  );
}

function TagsEditor({ tags, onChange }: { tags: string[]; onChange: (t: string[]) => void }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !tags.includes(v)) { onChange([...tags, v]); setInput(""); }
  };
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[#8aaec8] text-xs">标签</label>
      <div className="flex flex-wrap gap-1.5 min-h-[32px]">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-gray-300 text-xs border border-white/10">
            {tag}
            <button onClick={() => onChange(tags.filter((_, ti) => ti !== i))} className="text-gray-500 hover:text-red-400 cursor-pointer ml-0.5">
              <i className="ri-close-line text-xs" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="输入标签后按 Enter 或点添加"
          className="flex-1 bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
        />
        <button onClick={add} className="px-3 py-1.5 rounded-lg bg-[#4fc3f7]/10 border border-[#4fc3f7]/20 text-[#4fc3f7] text-xs cursor-pointer whitespace-nowrap hover:bg-[#4fc3f7]/20 transition-colors">
          添加
        </button>
      </div>
    </div>
  );
}

function SpecsEditor({ specs, onChange, lang }: { specs: ProductSpec[]; onChange: (s: ProductSpec[]) => void; lang: "zh" | "en" }) {
  const update = (idx: number, key: keyof ProductSpec, val: string) => {
    const next = [...specs];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...specs, { label: lang === "zh" ? "参数名" : "Spec Name", value: lang === "zh" ? "参数值" : "Value" }]);
  const remove = (idx: number) => onChange(specs.filter((_, i) => i !== idx));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[#8aaec8] text-xs">{lang === "zh" ? "技术规格" : "Specifications"}</label>
        <button onClick={add} className="flex items-center gap-1 text-[#4fc3f7] text-xs cursor-pointer hover:text-[#7dd3fc] transition-colors whitespace-nowrap">
          <i className="ri-add-line" />
          添加规格
        </button>
      </div>
      <div className="space-y-2">
        {specs.map((spec, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              type="text"
              value={spec.label}
              onChange={(e) => update(i, "label", e.target.value)}
              placeholder="参数名"
              className="flex-1 bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
            />
            <input
              type="text"
              value={spec.value}
              onChange={(e) => update(i, "value", e.target.value)}
              placeholder="参数值"
              className="flex-1 bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
            />
            <button onClick={() => remove(i)} className="w-6 h-6 flex items-center justify-center text-[#5a7a9a] hover:text-red-400 transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeaturesEditor({ features, onChange, lang }: { features: string[]; onChange: (f: string[]) => void; lang: "zh" | "en" }) {
  const update = (idx: number, val: string) => {
    const next = [...features];
    next[idx] = val;
    onChange(next);
  };
  const add = () => onChange([...features, lang === "zh" ? "新特性描述" : "New Feature"]);
  const remove = (idx: number) => onChange(features.filter((_, i) => i !== idx));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[#8aaec8] text-xs">{lang === "zh" ? "核心优势" : "Key Features"}</label>
        <button onClick={add} className="flex items-center gap-1 text-[#4fc3f7] text-xs cursor-pointer hover:text-[#7dd3fc] transition-colors whitespace-nowrap">
          <i className="ri-add-line" />
          添加优势
        </button>
      </div>
      <div className="space-y-2">
        {features.map((f, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="mt-2 text-[#4fc3f7] text-xs flex-shrink-0">
              <i className="ri-check-line" />
            </span>
            <input
              type="text"
              value={f}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors"
            />
            <button onClick={() => remove(i)} className="mt-1 w-6 h-6 flex items-center justify-center text-[#5a7a9a] hover:text-red-400 transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImagesEditor({ images, onChange, productId }: { images: string[]; onChange: (imgs: string[]) => void; productId: string }) {
  const update = (idx: number, val: string) => {
    const next = [...images];
    next[idx] = val;
    onChange(next);
  };
  const add = () => onChange([...images, ""]);
  const remove = (idx: number) => { if (images.length <= 1) return; onChange(images.filter((_, i) => i !== idx)); };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[#8aaec8] text-xs">产品图片（至少1张，最多3张）</label>
        {images.length < 3 && (
          <button onClick={add} className="flex items-center gap-1 text-[#4fc3f7] text-xs cursor-pointer hover:text-[#7dd3fc] transition-colors whitespace-nowrap">
            <i className="ri-add-line" />
            添加图片
          </button>
        )}
      </div>
      <div className="space-y-2">
        {images.map((img, i) => (
          <ImageField key={i} label={`图片 ${i + 1}`} value={img} onChange={(v) => update(i, v)} storageKey={`productsDetail.${productId}.images.${i}`} />
        ))}
      </div>
      {images.length > 1 && (
        <button
          onClick={() => remove(images.length - 1)}
          className="flex items-center gap-1.5 text-[#5a7a9a] hover:text-red-400 text-xs cursor-pointer transition-colors w-fit"
        >
          <i className="ri-delete-bin-line text-xs" />
          删除最后一张图片
        </button>
      )}
    </div>
  );
}

// ─── Category Manager ─────────────────────────────────────────────────────────

function CategoryManager({
  categories,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
}: {
  categories: ProductCategory[];
  onUpdateCategory: (idx: number, key: keyof ProductCategory, val: string) => void;
  onAddCategory: () => void;
  onRemoveCategory: (idx: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#0a1225] transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          <i className="ri-price-tag-3-line text-[#4fc3f7] text-sm" />
          <span className="text-white text-sm font-semibold">产品分类管理</span>
          <span className="ml-1 text-[#5a7a9a] text-xs">
            — 修改分类名称，自定义分类标签
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-2 py-0.5 rounded-full bg-[#4fc3f7]/10 text-[#4fc3f7] text-xs border border-[#4fc3f7]/20">
            {categories.length} 个分类
          </span>
          <i className={`text-[#5a7a9a] text-sm transition-transform duration-300 ${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#1e2d4a] p-5 space-y-4">
          <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3">
            <i className="ri-alert-line text-amber-400 text-sm flex-shrink-0 mt-0.5" />
            <p className="text-amber-200/80 text-xs leading-relaxed">
              分类 ID（如 <code className="text-amber-300">aero-engine</code>）仅在技术层面区分类别，不会显示给访客，无需修改。只需修改中英文名称即可。删除分类时，该分类下的产品会自动归入第一个分类。
            </p>
          </div>

          <div className="space-y-3">
            {categories.map((cat, i) => (
              <div key={cat.id} className="bg-[#070e1f] border border-[#1a2840] rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded bg-[#4fc3f7]/10 text-[#4fc3f7] text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="text-[#5a7a9a] text-xs font-mono">ID: {cat.id}</span>
                  </div>
                  <button
                    onClick={() => onRemoveCategory(i)}
                    disabled={categories.length <= 1}
                    title={categories.length <= 1 ? "至少保留一个分类" : "删除此分类"}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                      categories.length <= 1
                        ? "text-[#2a3a4a] cursor-not-allowed"
                        : "text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d]"
                    }`}
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldInput
                    label="中文分类名称"
                    value={cat.zh}
                    onChange={(v) => onUpdateCategory(i, "zh", v)}
                    placeholder="如：技术服务"
                  />
                  <FieldInput
                    label="英文分类名称"
                    value={cat.en}
                    onChange={(v) => onUpdateCategory(i, "en", v)}
                    placeholder="e.g. Tech Services"
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onAddCategory}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-[#4fc3f7]/40 text-[#4fc3f7] text-sm hover:bg-[#4fc3f7]/10 transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
          >
            <i className="ri-add-line text-base" />
            添加新分类
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Product ID Editor ────────────────────────────────────────────────────────

function ProductIdField({
  currentId,
  allIds,
  onSave,
}: {
  currentId: string;
  allIds: string[];
  onSave: (newId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(currentId);
  const [error, setError] = useState("");

  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  const validate = (val: string) => {
    if (!val.trim()) return "ID 不能为空";
    if (!slugRegex.test(val)) return "只允许小写字母、数字和连字符（-），如 my-engine";
    if (val !== currentId && allIds.includes(val)) return "此 ID 已被其他产品使用，请换一个";
    return "";
  };

  const handleSave = () => {
    const err = validate(inputVal.trim());
    if (err) { setError(err); return; }
    onSave(inputVal.trim());
    setEditing(false);
    setError("");
  };

  const handleCancel = () => {
    setInputVal(currentId);
    setEditing(false);
    setError("");
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-[#8aaec8] text-xs">产品 URL ID</label>
        {!editing && (
          <button
            onClick={() => { setEditing(true); setInputVal(currentId); setError(""); }}
            className="flex items-center gap-1 text-[#4fc3f7] text-xs cursor-pointer hover:text-[#7dd3fc] transition-colors whitespace-nowrap"
          >
            <i className="ri-edit-line text-xs" />
            修改
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <span className="text-[#5a7a9a] text-xs whitespace-nowrap">/products/</span>
            <input
              type="text"
              value={inputVal}
              onChange={(e) => {
                setInputVal(e.target.value);
                setError(validate(e.target.value));
              }}
              placeholder="my-engine"
              className={`flex-1 bg-[#0a1628] border rounded-lg px-3 py-2 text-white text-xs focus:outline-none transition-colors ${
                error ? "border-red-500/60 focus:border-red-500" : "border-[#1e2d4a] focus:border-[#4fc3f7]"
              }`}
            />
            <button
              onClick={handleSave}
              className="px-3 py-2 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              确认
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-[#5a7a9a] text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
          </div>
          {error && (
            <p className="flex items-center gap-1 text-red-400 text-xs">
              <i className="ri-error-warning-line" />
              {error}
            </p>
          )}
          <p className="text-[#5a7a9a] text-xs">
            格式：只允许小写字母、数字和连字符。如 <code className="text-[#4fc3f7]">xh-tf200</code> → 访问 <code className="text-[#4fc3f7]">/products/xh-tf200</code>
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2">
          <span className="text-[#5a7a9a] text-xs">/products/</span>
          <span className="text-[#4fc3f7] text-xs font-mono font-semibold">{currentId}</span>
          <span className="ml-auto text-[#3a5a7a] text-[10px] bg-white/5 px-1.5 py-0.5 rounded border border-white/5">URL路径</span>
        </div>
      )}
    </div>
  );
}

// ─── Single product editor panel ──────────────────────────────────────────────

function ProductDetailPanel({
  product,
  idx,
  onUpdate,
  onRemove,
  canRemove,
  categoryOptions,
  allProductIds,
  onUpdateId,
}: {
  product: ProductDetail;
  idx: number;
  onUpdate: (updater: (p: ProductDetail) => ProductDetail) => void;
  onRemove: () => void;
  canRemove: boolean;
  categoryOptions: { id: string; zh: string; en: string }[];
  allProductIds: string[];
  onUpdateId: (newId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editLang, setEditLang] = useState<"zh" | "en">("zh");

  const locale = editLang === "zh" ? product.zh : product.en;

  const setLocale = (key: keyof typeof product.zh, val: unknown) => {
    onUpdate((p) => ({ ...p, [editLang]: { ...p[editLang], [key]: val } }));
  };

  const accentColor = ACCENT_OPTIONS.find((a) => a.value === product.accent) || ACCENT_OPTIONS[0];

  return (
    <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl overflow-hidden">
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#0a1225] transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-6 h-6 flex items-center justify-center rounded bg-[#4fc3f7]/10 text-[#4fc3f7] text-xs font-bold flex-shrink-0">
            {idx + 1}
          </span>
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{product.zh.title}</p>
            <p className="text-[#5a7a9a] text-xs truncate">{product.en.title}</p>
          </div>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs border ${
            product.accent === "cyan" ? "border-cyan-500/30 text-cyan-400 bg-cyan-500/10" :
            product.accent === "orange" ? "border-orange-500/30 text-orange-400 bg-orange-500/10" :
            product.accent === "emerald" ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" :
            "border-violet-500/30 text-violet-400 bg-violet-500/10"
          }`}>
            {categoryOptions.find((c) => c.id === product.category)?.zh ?? product.category}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer"
              title="删除此产品"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          )}
          <i className={`text-[#5a7a9a] text-sm transition-transform duration-300 ${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
        </div>
      </div>

      {/* Expanded edit panel */}
      {expanded && (
        <div className="border-t border-[#1e2d4a] p-5 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-[#8aaec8] text-xs">所属分类</label>
              <select
                value={product.category}
                onChange={(e) => onUpdate((p) => ({ ...p, category: e.target.value }))}
                className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] cursor-pointer"
              >
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.zh} / {c.en}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[#8aaec8] text-xs">主题色</label>
              <div className="flex gap-2">
                {ACCENT_OPTIONS.map((a) => (
                  <button
                    key={a.value}
                    onClick={() => onUpdate((p) => ({ ...p, accent: a.value }))}
                    title={a.label}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-all whitespace-nowrap ${
                      product.accent === a.value
                        ? "border-white/30 bg-white/10 text-white"
                        : "border-[#1e2d4a] text-[#5a7a9a] hover:border-white/20"
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${a.cls}`} />
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Product ID */}
          <div className="bg-[#070e1f] rounded-xl p-4 border border-[#1a2840]">
            <ProductIdField
              currentId={product.id}
              allIds={allProductIds}
              onSave={onUpdateId}
            />
          </div>

          {/* Language switcher */}
          <div className="flex items-center gap-1 bg-[#070c1a] rounded-lg p-1 w-fit border border-[#1e2d4a]">
            <button
              onClick={() => setEditLang("zh")}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                editLang === "zh" ? "bg-[#4fc3f7]/20 text-[#4fc3f7]" : "text-[#5a7a9a] hover:text-white"
              }`}
            >
              中文
            </button>
            <button
              onClick={() => setEditLang("en")}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                editLang === "en" ? "bg-[#4fc3f7]/20 text-[#4fc3f7]" : "text-[#5a7a9a] hover:text-white"
              }`}
            >
              English
            </button>
          </div>

          {/* Locale fields */}
          <div className="space-y-4 bg-[#070e1f] rounded-xl p-4 border border-[#1a2840]">
            <FieldInput
              label={editLang === "zh" ? "产品名称" : "Product Name"}
              value={locale.title}
              onChange={(v) => setLocale("title", v)}
            />
            <FieldInput
              label={editLang === "zh" ? "副标题" : "Subtitle"}
              value={locale.subtitle}
              onChange={(v) => setLocale("subtitle", v)}
            />
            <FieldInput
              label={editLang === "zh" ? "产品描述" : "Description"}
              value={locale.desc}
              onChange={(v) => setLocale("desc", v)}
              multiline
            />
            <TagsEditor tags={locale.tags} onChange={(t) => setLocale("tags", t)} />
            <SpecsEditor specs={locale.specs} onChange={(s) => setLocale("specs", s)} lang={editLang} />
            <FeaturesEditor features={locale.features} onChange={(f) => setLocale("features", f)} lang={editLang} />
          </div>

          {/* Images */}
          <div className="bg-[#070e1f] rounded-xl p-4 border border-[#1a2840]">
            <ImagesEditor
              images={product.images}
              onChange={(imgs) => onUpdate((p) => ({ ...p, images: imgs }))}
              productId={product.id}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function ProductDetailEditorSection() {
  const {
    content,
    addProductDetail,
    removeProductDetail,
    updateProductDetail,
    updateProductCategory,
    addProductCategory,
    removeProductCategory,
    updateProductId,
  } = useContentStore();

  const allProductIds = content.productsDetail.map((p) => p.id);

  return (
    <div className="space-y-4">
      {/* Category Manager */}
      <CategoryManager
        categories={content.productCategories}
        onUpdateCategory={updateProductCategory}
        onAddCategory={addProductCategory}
        onRemoveCategory={removeProductCategory}
      />

      {/* Tip */}
      <div className="flex items-start gap-2 bg-[#4fc3f7]/5 border border-[#4fc3f7]/20 rounded-lg px-4 py-3">
        <i className="ri-information-line text-[#4fc3f7] text-sm flex-shrink-0 mt-0.5"></i>
        <p className="text-[#8aaec8] text-xs leading-relaxed">
          点击产品卡片展开编辑。支持修改中英文名称、描述、标签、规格参数、核心优势、产品图片及 URL 路径。
          修改后点「保存内容」即可生效。
        </p>
      </div>

      {/* Product list */}
      <div className="space-y-3">
        {content.productsDetail.map((p, i) => (
          <ProductDetailPanel
            key={p.id}
            product={p}
            idx={i}
            onUpdate={(updater) => updateProductDetail(i, updater)}
            onRemove={() => removeProductDetail(i)}
            canRemove={content.productsDetail.length > 1}
            categoryOptions={content.productCategories}
            allProductIds={allProductIds}
            onUpdateId={(newId) => updateProductId(i, newId)}
          />
        ))}
      </div>

      {/* Add button */}
      <button
        onClick={addProductDetail}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-[#4fc3f7]/40 text-[#4fc3f7] text-sm hover:bg-[#4fc3f7]/10 transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
      >
        <i className="ri-add-line text-base"></i>
        添加新产品
      </button>
    </div>
  );
}
